import { Base_Unit_Type_Enum, Brand_Enum, Business_Profile_Enum, Minimum_Order_Value_Type_Enum } from '@kheops/graphql';
import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { getConvertedQuantity, round } from '@kheops/utils';
import { BasketItem } from '../basket/state/basket';
import FileUtils, { PhotoSize } from '../common/utils/file.utils';
import { contractsAtom, currentContextAtom } from '../state';

export interface BasketOrder {
  items: BasketItem[];
  contractId: string;
  photoUrl: string;
  name: string;
  companyId: string;
  minimumOrderValue: number;
  minimumOrderValueType: Minimum_Order_Value_Type_Enum;
  minimumOrderValueProgression: number;
  totalPrice: number;
  brand?: Brand_Enum | null;
}

export interface UseBasketOrdersParams {
  basketItems: BasketItem[];
  photoSize?: PhotoSize;
}

export interface BasketOrdersDict {
  [contractId: string]: BasketOrder,
}

export function useBasketOrders({ basketItems, photoSize }: UseBasketOrdersParams): BasketOrdersDict {
  const { realm } = useAtomValue(currentContextAtom);
  const contracts = useAtomValue(contractsAtom);

  const getMinimumOrderValueProgression = (basketItem: BasketItem, minimumOrderValueType: Minimum_Order_Value_Type_Enum): number => {
    switch (minimumOrderValueType) {
      case Minimum_Order_Value_Type_Enum.Weight: {
        const convertedNetContent = basketItem.packaging.base_unit?.unit_type === Base_Unit_Type_Enum.Bulk
          ? getConvertedQuantity(basketItem.packaging.net_content!, basketItem.packaging.content_measurement_unit!).value
          : getConvertedQuantity(basketItem.packaging.base_unit!.unit_net_content!, basketItem.packaging.base_unit!.content_measurement_unit!).value * basketItem.packaging.unit_quantity!;

        return convertedNetContent * basketItem.quantity;
      }
      case Minimum_Order_Value_Type_Enum.Packaging:
        return basketItem.quantity;
      default:
        return basketItem.quantity * basketItem.packaging.price;
    }
  };

  return useMemo(() => {
    if (!contracts.length) {
      return {};
    }

    return basketItems.reduce(
      (acc, item) => {
        const contractId = item.contract_id;
        const contract = contracts.find(({ reference_id }) => contractId === reference_id)!;
        const customPriceListDiscount = contract.custom_price_list?.custom_price_list_packagings
          .find(({ packaging_sku }) => packaging_sku === item.packaging.sku)
          ?.discount;
        const adjustedItem: BasketItem = {
          ...item,
          packaging: {
            ...item.packaging,
            price: round(item.packaging.price * (1 - (customPriceListDiscount || item.contract.discount)), 3),
          },
        };
        const minimumOrderValueProgression = getMinimumOrderValueProgression(adjustedItem, item.contract.minimum_order_value_type);
        const price = adjustedItem.quantity * adjustedItem.packaging.price;

        if (!acc[contractId]) {
          const { id, tradeName, photos, business_profile, brand } = realm === Business_Profile_Enum.Buyer
            ? adjustedItem.contract.supplying_company
            : adjustedItem.contract.buying_company;

          acc[contractId] = {
            items: [adjustedItem],
            contractId,
            photoUrl: FileUtils.BuildCompanyImageUrl({
              photo: photos[0]?.photo,
              size: photoSize || { height: 112, width: 112 },
              type: business_profile,
            }),
            companyId: id,
            name: tradeName,
            brand,
            minimumOrderValue: adjustedItem.contract.minimum_order_value!,
            minimumOrderValueType: adjustedItem.contract.minimum_order_value_type!,
            minimumOrderValueProgression,
            totalPrice: price,
          };
        } else {
          acc[contractId].items.push(adjustedItem);
          acc[contractId].minimumOrderValueProgression += minimumOrderValueProgression;
          acc[contractId].totalPrice += price;
        }

        return acc;
      },
      {} as BasketOrdersDict,
    );
  }, [basketItems, contracts]);
}
